import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { STATUS_META } from "@/lib/constants";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { BarChart3, FileText, Newspaper, Palette, Clock, ShieldQuestion, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return `hsl(${fallback})`;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return `hsl(${v || fallback})`;
};

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const CHART_COLORS = [
    cssVar("--chart-1", "150 32% 30%"), cssVar("--chart-2", "344 45% 64%"),
    cssVar("--chart-3", "43 40% 54%"), cssVar("--chart-4", "150 16% 54%"),
    cssVar("--chart-5", "338 22% 64%"), cssVar("--chart-6", "44 26% 66%"),
    cssVar("--chart-7", "152 12% 46%"),
  ];
  const barPrimary = cssVar("--chart-1", "150 32% 30%");
  const barAccent = cssVar("--chart-2", "344 45% 64%");

  const stats = [
    { label: "Total submissions", value: data.total_submissions, icon: FileText },
    { label: "Sunday News", value: data.sunday_news, icon: Newspaper },
    { label: "Communications", value: data.comms_requests, icon: Palette },
    { label: "Published", value: data.published, icon: CheckCircle2 },
    { label: "Avg completion (days)", value: data.avg_completion_days, icon: Clock },
    { label: "Awaiting approval", value: data.awaiting_approval, icon: ShieldQuestion },
    { label: "Returned (missing info)", value: data.returned_missing_info, icon: AlertCircle },
  ];

  const statusData = data.by_status.map((s) => ({ name: STATUS_META[s.name]?.label || s.name, value: s.value }));
  const prettyType = (n) => (n === "sunday_news" ? "Sunday News" : n === "comms_request" ? "Communications" : n);
  const typeData = data.by_type.map((t) => ({ ...t, name: prettyType(t.name) }));

  const exportCsv = () => {
    const rows = [["Metric", "Value"],
      ["Total submissions", data.total_submissions], ["Sunday News", data.sunday_news],
      ["Communications requests", data.comms_requests], ["Published", data.published],
      ["Avg completion days", data.avg_completion_days], ["Awaiting approval", data.awaiting_approval],
      ["Returned missing info", data.returned_missing_info], [],
      ["Committee", "Submissions"], ...data.by_committee.map((c) => [c.name, c.value]), [],
      ["Service Type", "Requests"], ...data.by_type.map((t) => [t.name, t.value])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "commconnect-report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><BarChart3 className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Reports</h1>
            <p className="text-sm text-muted-foreground">Submission volume, workload & performance.</p>
          </div>
        </div>
        <button onClick={exportCsv} data-testid="export-report-btn" className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
        {stats.map((s) => (
          <Card key={s.label} className="p-4" data-testid={`report-stat-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <s.icon className="h-4 w-4 text-primary" />
            <p className="mt-2 font-heading text-2xl font-bold">{s.value}</p>
            <p className="text-xs leading-tight text-muted-foreground">{s.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="font-heading text-lg font-semibold">Requests by committee</h2>
          <div className="mt-4 h-72" data-testid="chart-committee">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_committee} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 14% 87%)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} />
                <Tooltip />
                <Bar dataKey="value" fill={barPrimary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-heading text-lg font-semibold">Requests by service type</h2>
          <div className="mt-4 h-72" data-testid="chart-type">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeData} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(42 14% 87%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill={barAccent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold">Pipeline by status</h2>
          <div className="mt-4 flex flex-col items-center gap-6 lg:flex-row">
            <div className="h-72 w-full max-w-xs" data-testid="chart-status">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {statusData.map((s, i) => <Cell key={s.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-3">
              {statusData.map((s, i) => (
                <div key={s.name} className="flex items-center gap-2 text-sm">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="flex-1 truncate text-muted-foreground">{s.name}</span>
                  <span className="font-semibold">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
